import { useFormContext } from 'react-hook-form';
import { FormHiddenInput, FormSelectField, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Select } from '@optimus-web/core';
import { createListItems } from '../../../utils';
import { useLearningPathsOptions } from '../../../hooks/useCommonFormOptions';
import { LockedFormProps, MULTIPLE_VALUES } from '../../../types';
import { FieldNames } from '../../FieldNames';
import { EditUserFormValues } from '../schema';
import { LockableField } from './LockableField';

/* This component uses "dumb", unbound, and disabled multiselect <Select> to show multiple learning paths when
   the field is locked, and react-hook-form bound <FormSelectField> when unlocked.
   A [name={FieldNames.AssignedLearningPath}] input needs to be mounted so that react-hook-form
   correctly recognizes when the form is dirty.
*/

export const LearningPathLockableField = ({
    fieldsConfig,
    detailedUser,
    lockedFields,
    setLockedFields,
    isUserProvisioned,
}: LockedFormProps) => {
    const lockable = !!fieldsConfig?.includes('equalLearningPaths');
    const learningPathsOptions = useLearningPathsOptions();
    const { setValue } = useFormContext<EditUserFormValues>();

    const setLearningPathLocked = (locked: boolean) => {
        setLockedFields?.({ ...lockedFields, [FieldNames.AssignedLearningPath]: !locked });
        setValue(FieldNames.AssignedLearningPath, locked ? undefined : MULTIPLE_VALUES, { shouldDirty: true });
    };
    const fieldLocked = !!lockedFields?.[FieldNames.AssignedLearningPath] || !!isUserProvisioned;
    return (
        <LockableField
            locked={fieldLocked}
            lockable={lockable}
            tooltipTextKey={TextKey.UserManagementUnlockField}
            onLockToggle={() => setLearningPathLocked(fieldLocked)}
        >
            {fieldLocked ? (
                <>
                    <Select
                        name="lockedLearningPath"
                        label={<LocalizedText textKey={TextKey.LearningPath} />}
                        helper={<LocalizedText textKey={TextKey.UserManagementValuesAreDifferentField} />}
                        selected={detailedUser?.learningPaths || []}
                        multiselect
                        required
                        disabled
                    >
                        {createListItems(learningPathsOptions)}
                    </Select>
                    <FormHiddenInput name={FieldNames.AssignedLearningPath} />
                </>
            ) : (
                <FormSelectField
                    name={FieldNames.AssignedLearningPath}
                    label={<LocalizedText textKey={TextKey.LearningPath} />}
                    helper={<LocalizedText textKey={TextKey.LearningPathHelper} />}
                    required
                >
                    {createListItems(learningPathsOptions)}
                </FormSelectField>
            )}
        </LockableField>
    );
};
